import {
  DASHBOARD_SET_ACTIONABLES_IS_FETCHING,
  DASHBOARD_FETCH_ACTIONABLES,
  DASHBOARD_FETCH_ASSISTANCE_REQUESTS,
  DASHBOARD_FETCH_CASES,
  DASHBOARD_FETCH_CONTACTS,
  DASHBOARD_FETCH_REFERRALS,
  DASHBOARD_UNSET_ACTIONABLES_IS_FETCHING,
  REMOVE_DELETED_DRAFT_REFERRAL,
  RESET_DASHBOARD,
  SET_DASHBOARD_FETCHING,
  SET_DASHBOARD_REFETCH,
  UNSET_DASHBOARD_FETCHING,
  UNSET_REFETCH_ACTIONABLES,
  USER_LOGOUT,
  WS_DECREMENT_COUNT,
  WS_HANDLE_MESSAGE,
  WS_INCREMENT_COUNT,
  REASSIGN_REFERRAL_PROGRAM,
  DASHBOARD_FETCH_SCREENINGS,
  DASHBOARD_FETCH_EXTERNAL_SCREENINGS,
} from 'actions';
import socketHandler from 'src/components/Dashboard/utils/socketHandler';
import _ from 'lodash';
import moment from 'moment';

const defaultTarget = {
  data: [
    { data: [], paging: {}, time: 0 },
  ],
  currentPage: 1,
  filters: {},
  isFetching: false,
  cancel: null,
};

const defaultData = [
  {
    data: [],
    paging: {},
    time: 0,
  },
];

const defaultState = {
  actionables: {},
  allCases: defaultTarget,
  allNetworkCases: defaultTarget,
  assistanceRequests: defaultTarget,
  clients: defaultTarget,
  closedAssistanceRequests: defaultTarget,
  closedCases: defaultTarget,
  closedReferrals: defaultTarget,
  closedInboundReferrals: defaultTarget,
  draftReferrals: defaultTarget,
  intakes: defaultTarget,
  isActionablesFetching: false,
  newReferrals: defaultTarget,
  openCases: defaultTarget,
  pendingConsentReferrals: defaultTarget,
  processedAssistanceRequests: defaultTarget,
  recalledReferrals: defaultTarget,
  referralsInReview: defaultTarget,
  refetch: false,
  refetchActionables: false,
  rejectedReferrals: defaultTarget,
  screeningsAll: defaultTarget,
  sentReferrals: defaultTarget,
  sentReferralsAcceptedClosed: defaultTarget,
  sentReferralsAcceptedOpen: defaultTarget,
  sentReferralsAll: defaultTarget,
  sentReferralsInReview: defaultTarget,
  sentReferralsPending: defaultTarget,
  sentReferralsPendingConsent: defaultTarget,
};

function cancelPreviousRequestIfExists(state, action) {
  const cancel = _.get(state, [action.target, 'cancel'], null);
  if (cancel !== null) {
    cancel();
  }
}

function updateState(state, action) {
  const currentData = _.get(state, action.target, {});
  const currentPage = _.get(action, 'payload.data.paging.current_page', 1);
  let newData = _.assign([], currentData.data);
  if (!_.isEqual(_.get(currentData, 'filters', {}), action.filters)) {
    newData = [{ data: [], paging: {} }];
  }
  newData[currentPage - 1] = {
    data: action.payload.data.data,
    paging: action.payload.data.paging,
    time: moment().unix(),
  };
  return _.assign({}, state, {
    [action.target]: {
      data: newData,
      filters: action.filters,
      isFetching: state[action.target].isFetching,
      currentPage: action.payload.data.paging.current_page,
    },
  });
}

function updateFetching(state, action, unset = false) {
  const currentData = _.get(state, action.target, {});
  const isFetching = !unset;
  const reset = isFetching && !_.isEqual(currentData.filters, action.filters);

  return {
    ...state,
    [action.target]: {
      data: reset ? defaultData : currentData.data,
      filters: currentData.filters,
      isFetching,
      cancel: unset ? null : action.cancel,
      currentPage: reset ? 1 : _.get(currentData, 'currentPage', 1),
    },
  };
}

function doRecursiveAssignAt(state, value, paths) {
  if (_.isEmpty(_.tail(paths))) {
    return _.assign({}, state, { [_.head(paths)]: value });
  }

  return _.assign({}, state, {
    [_.head(paths)]: doRecursiveAssignAt(
      _.get(state, _.head(paths), {}),
      value,
      _.tail(paths),
    ),
  });
}

function recursiveAssignAt(state, value, paths) {
  if (_.isString(paths)) {
    return doRecursiveAssignAt(state, value, _.split(paths, '.'));
  }

  return doRecursiveAssignAt(state, value, paths);
}

function updateDashboardData(state, action) {
  return {
    ...updateState(state, action),
    refetch: false,
  };
}

function updateExternalScreenings(state, action) {
  return {
    ...updateDashboardData(state, action),
    screeningsExtraData: { templates: action.extraData.templates },
  };
}

function resetDashboard() {
  return { ...defaultState };
}

function updateActionables(state, action) {
  if (action.updated < state.actionablesUpdated) {
    return state;
  }

  return {
    ...state,
    actionables: action.payload.data.data,
    actionablesUpdated: action.updated,
  };
}

function updateActionablesRefetch(state) {
  return { ...state, refetchActionables: false };
}

function setDashboardRefetch(state) {
  return { ...state, refetch: true, refetchActionables: true };
}

function updateActionableCount(state, action, delta) {
  const path = `actionables.${action.target}`;
  const value = _.get(state, path, 0) + delta;

  return recursiveAssignAt(state, value, path);
}

function incrementActionableCount(state, action) {
  return updateActionableCount(state, action, 1);
}

function decrementActionableCount(state, action) {
  return updateActionableCount(state, action, -1);
}

function handleSocketMessage(state, action) {
  return socketHandler(state, action.payload, action.currentSection, action.currentGroup);
}

function setActionablesFetching(state) {
  return { ...state, isActionablesFetching: true };
}

function unsetActionablesFetching(state) {
  return { ...state, isActionablesFetching: false };
}

function updateTargetPageData(target, updateData) {
  const nextData = _.transform(
    target.data,
    (acc, pageData, key) => {
      acc[key] = {
        ...pageData,
        data: updateData(_.get(pageData, 'data', [])),
      };
    },
    _.isArray(target.data) ? [] : {},
  );

  return {
    ...target,
    data: nextData,
  };
}

function removeDeletedDraftReferral(state, action) {
  if (!action.draftId) {
    return state;
  }

  return {
    ...state,
    draftReferrals: updateTargetPageData(
      state.draftReferrals,
      (data) => _.reject(data, { id: action.draftId }),
    ),
  };
}

function reassignReferralProgram(state, action) {
  const referral = action.payload.data.data;

  return {
    ...state,
    newReferrals: updateTargetPageData(
      state.newReferrals,
      (data) => _.map(data, (dataReferral) => (
        _.isEqual(dataReferral.id, referral.id) ? referral : dataReferral
      )),
    ),
  };
}

const ACTION_HANDLERS = {
  [SET_DASHBOARD_FETCHING]: (state, action) => {
    cancelPreviousRequestIfExists(state, action);
    return updateFetching(state, action);
  },
  [UNSET_DASHBOARD_FETCHING]: (state, action) => updateFetching(state, action, true),
  [DASHBOARD_FETCH_SCREENINGS]: updateDashboardData,
  [DASHBOARD_FETCH_REFERRALS]: updateDashboardData,
  [DASHBOARD_FETCH_ASSISTANCE_REQUESTS]: updateDashboardData,
  [DASHBOARD_FETCH_CASES]: updateDashboardData,
  [DASHBOARD_FETCH_CONTACTS]: updateDashboardData,
  [DASHBOARD_FETCH_EXTERNAL_SCREENINGS]: updateExternalScreenings,
  [USER_LOGOUT]: resetDashboard,
  [RESET_DASHBOARD]: resetDashboard,
  [DASHBOARD_FETCH_ACTIONABLES]: updateActionables,
  [UNSET_REFETCH_ACTIONABLES]: updateActionablesRefetch,
  [SET_DASHBOARD_REFETCH]: setDashboardRefetch,
  [WS_INCREMENT_COUNT]: incrementActionableCount,
  [WS_DECREMENT_COUNT]: decrementActionableCount,
  [WS_HANDLE_MESSAGE]: handleSocketMessage,
  [DASHBOARD_SET_ACTIONABLES_IS_FETCHING]: setActionablesFetching,
  [DASHBOARD_UNSET_ACTIONABLES_IS_FETCHING]: unsetActionablesFetching,
  [REMOVE_DELETED_DRAFT_REFERRAL]: removeDeletedDraftReferral,
  [REASSIGN_REFERRAL_PROGRAM]: reassignReferralProgram,
};

export default function dashboardReducer(state = defaultState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}
